/**
 * Is this a plain old object and not an array
 * @param  {*} val      Any JavaScript value
 * @return {Boolean}    True, if the val is a plain old object; false, otherwise.
 */
export const isObj = (val) => _.isObject(val) && !_.isArray(val);

/**
 * Creates a base reducer for Enums that only need to
 * merge their payload with the state. This base reducer
 * does offer an override for a 'RESET' action, to
 * completely reset the state.
 * @param  {Object} enumSet Hash of available enums
 * @return {Object}         Reduced state
 */
export function createEnumReducer(enumSet) {
    return (state, action) => {
        if (enumSet[action.type]) {
            // is this a reset action?
            // if so, wipe state clean
            if (action.type === 'RESET') {
                return {};
            }

            return merge(
                state,
                action.payload
            );
        }

        // no match, passthrough state for next reducer
        return state;
    };
}

/**
 * A customizer function for mixing together two objects.
 * If both the dest and src values are objects, then
 * this customizer will mixin the two values. If neither
 * values are objects, or if only one is an object, then
 * this customizer will take the src value.
 * @param  {Object} dest Destination object for customizer values
 * @param  {Object} src  Source object for customizer values
 * @return {*}          customizer value
 */
export function customizer(dest, src) {
    if (isObj(dest) && isObj(src)) {
        return mergeWith(customizer, dest, src);
    }

    return src;
}

/**
 * Create a new object and mix a list of new objects into it.
 * Accepts a custom customizer for specifying how to mix an object
 * into the new object.
 * @param  {Function} customizer    Resolve merge callback
 * @param  {Object} objs            Any number of objects to be mixed-in
 * @return {Object}                 New object with all given objects mixed-in.
 */
// eslint-disable-next-line no-shadow
export function mergeWith(customizer, ...objs) {
    return objs.reduce(
        (copy, obj) => {
            if (isObj(obj)) {
                Object.keys(obj)
                    .forEach((key) => {
                        copy[key] = customizer(copy[key], obj[key]);
                    });
            }

            return copy;
        },
        {}
    );
}

/**
 * Merge a list of objects together into
 * a new, frozen object.
 * @param  {Object} objs    List of objects to be merged
 * @return {Object}         A newly merged, frozen object
 */
export function merge(...objs) {
    const merged = mergeWith(customizer, ...objs);

    return Object.freeze(merged);
}

/**
 * Mixin in a list of objects into a new, frozen object
 * by giving precedence to the last object passed-in,
 * so that any attribute will be overwritten.
 * @param  {Object} objs    List of objects to be mixed in
 * @return {Object}         A newly merged, frozen object
 */
export function mixin(...objs) {
    return Object.freeze(Object.assign({}, ...objs));
}

/**
 * Merge a list of objects, and then assign them to an array
 * at a given index.
 * @param  {Array} arr              Array to update
 * @param  {Number} index           Array index to update
 * @param  {Object} objs            List of objects to be merged
 * @return {Array}                  Newly updated array with the merged object at the given index
 */
export function reduceObjectInArray(arr, index, ...objs) {
    arr[index] = merge(...objs);
    return arr;
}

export function reduceOneObjectInArray(arr, index, obj) {
    return [...arr.slice(0, index), Object.assign({}, obj), ...arr.slice(index + 1)];
}
